import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Package, Truck, Ship, Box, Container, ArrowRight } from 'lucide-react';

export default function ContainerChart() {
  // ---------------- Dados ----------------
  const containerData = [
    { year: 2010, tu: 2.6, tku: 1.8 },
    { year: 2011, tu: 2.4, tku: 1.6 },
    { year: 2012, tu: 2.4, tku: 1.7 },
    { year: 2013, tu: 2.9, tku: 2.3 },
    { year: 2014, tu: 3.6, tku: 2.7 },
    { year: 2015, tu: 3.5, tku: 2.3 },
    { year: 2016, tu: 3.7, tku: 2.4 },
    { year: 2017, tu: 3.8, tku: 2.8 },
    { year: 2018, tu: 4.3, tku: 3.1 },
    { year: 2019, tu: 5.1, tku: 3.7 },
    { year: 2020, tu: 5.1, tku: 3.7 },
    { year: 2021, tu: 5.3, tku: 4.2 },
    { year: 2022, tu: 5.7, tku: 4.5 },
    { year: 2023, tu: 5.9, tku: 4.4 },
    { year: 2024, tu: 6.3, tku: 4.9 },
  ];

  // ---------------- Estado/refs ----------------
  const svgRef = useRef(null);
  const scrollRef = useRef(null);           // wrapper que rola no mobile
  const containerRef = useRef(null);        // container RELATIVE (tooltip fica absolute dentro)
  const tooltipRef = useRef(null);

  const [tooltip, setTooltip] = useState(null);       // {index, year, tu, tku, cx, cy}
  const [tooltipPos, setTooltipPos] = useState(null); // {left, top, placeAbove}
  const [isMobile, setIsMobile] = useState(false);

  // transform horizontal x' = tx + s*x
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => { if (isMobile) setTx(0); }, [isMobile]);

  // ---------------- Layout SVG ----------------
  const VB_W = 1200;
  const VB_H = 360;

  const MARGINS = useMemo(
    () => (isMobile
      ? { left: 36, right: 18, top: 26, bottom: 52 }
      : { left: 70, right: 60, top: 36, bottom: 64 }),
    [isMobile]
  );

  const X0 = MARGINS.left, X1 = VB_W - MARGINS.right;
  const Y0 = MARGINS.top,  Y1 = VB_H - MARGINS.bottom;
  const CHART_W = X1 - X0;
  const CHART_H = Y1 - Y0;

  const maxTu  = useMemo(() => Math.max(...containerData.map(d => d.tu)), []);
  const maxTku = useMemo(() => Math.max(...containerData.map(d => d.tku)), []);

  const stepX = useMemo(() => CHART_W / (containerData.length - 1), [CHART_W, containerData.length]);
  const xAt   = (i) => X0 + i * stepX;
  const yTu   = (v) => Y1 - (v / maxTu)  * CHART_H;
  const yTku  = (v) => Y1 - (v / maxTku) * CHART_H;

  // ---------------- Utils de coordenadas ----------------
  function svgMetrics() {
    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    const meet = Math.min(rect.width / VB_W, rect.height / VB_H);
    const xOff = (rect.width  - VB_W * meet) / 2;
    const yOff = (rect.height - VB_H * meet) / 2;
    return { rect, meet, xOff, yOff };
  }
  function clientToSvgXY(clientX, clientY) {
    const { rect, meet, xOff, yOff } = svgMetrics();
    return {
      xSvg: (clientX - rect.left - xOff) / meet,
      ySvg: (clientY - rect.top  - yOff) / meet,
      meet, xOff, yOff
    };
  }
  function xSvgToIndex(xSvgRaw) {
    const xSvg = (xSvgRaw - tx) / scale;
    const raw = (xSvg - X0) / stepX;
    return Math.min(containerData.length - 1, Math.max(0, Math.round(raw)));
  }

  // ---------------- Tooltip seguro ----------------
  const PADDING = 8;

  function updateTooltipSafePosition(next) {
    if (!containerRef.current || !tooltipRef.current) return;
    const { meet, xOff, yOff } = svgMetrics();

    // ponto de referência (entre as séries) em SVG -> px
    const cxT = tx + scale * next.cx;
    const leftCenter = xOff + cxT * meet;
    const topAbove = yOff + (next.cy - 10) * meet;
    const topBelow = yOff + (next.cy + 10) * meet;

    const contRect = containerRef.current.getBoundingClientRect();
    const tipW = tooltipRef.current.offsetWidth;
    const tipH = tooltipRef.current.offsetHeight;

    // preferência: desktop acima, mobile abaixo
    let placeAbove = !isMobile;
    let top = placeAbove ? topAbove - tipH : topBelow;

    if (placeAbove && top < PADDING) { placeAbove = false; top = topBelow; }
    if (!placeAbove && top + tipH > contRect.height - PADDING) { placeAbove = true; top = topAbove - tipH; }

    const minCenter = PADDING + tipW/2;
    const maxCenter = contRect.width - PADDING - tipW/2;
    const left = Math.max(minCenter, Math.min(maxCenter, leftCenter));

    setTooltipPos({ left, top, placeAbove });
  }

  function setTooltipFromClient(clientX, clientY) {
    const { xSvg } = clientToSvgXY(clientX, clientY);
    const idx = xSvgToIndex(xSvg);
    const d = containerData[idx];
    const cx = xAt(idx);
    const cy = Math.min(yTu(d.tu), yTku(d.tku)); // ponto entre as linhas
    const next = { index: idx, year: d.year, tu: d.tu, tku: d.tku, cx, cy };
    setTooltip(next);
  }

  useEffect(() => {
    if (!tooltip) return;
    requestAnimationFrame(() => updateTooltipSafePosition(tooltip));
  }, [tooltip, isMobile, scale, tx]);

  // reacomoda ao rolar o wrapper (mobile)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => { if (tooltip) updateTooltipSafePosition(tooltip); };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [tooltip]);

  const handleLeave = () => { setTooltip(null); setTooltipPos(null); };

  // ---------------- Zoom & Pan (desktop) ----------------
  const MIN_SCALE = 1, MAX_SCALE = 4;
  const boundsFor = (s) => ({ minTx: X1*(1-s), maxTx: X0*(1-s) });
  const clampTx = (t, s) => {
    const { minTx, maxTx } = boundsFor(s);
    return Math.min(maxTx, Math.max(minTx, t));
  };
  function applyZoom(factor, xCenterSvg) {
    const sNew = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale * factor));
    let tNew = tx + scale * xCenterSvg - sNew * xCenterSvg;
    if (!isMobile) tNew = clampTx(tNew, sNew); else tNew = 0;
    setScale(sNew); setTx(tNew);
  }
  function handleWheel(e) {
    if (!e.ctrlKey) return;
    e.preventDefault();
    const { xSvg } = clientToSvgXY(e.clientX, e.clientY);
    const f = e.deltaY < 0 ? 1.1 : 1/1.1;
    applyZoom(f, xSvg);
  }
  const dragRef = useRef({ active:false, x0:0, tx0:0 });
  function onMouseDown(e){
    if (isMobile) return;
    e.preventDefault();
    const { xSvg } = clientToSvgXY(e.clientX, e.clientY);
    dragRef.current = { active:true, x0:xSvg, tx0:tx };
  }
  function onMouseMove(e){
    if (!dragRef.current.active || isMobile) return;
    const { xSvg } = clientToSvgXY(e.clientX, e.clientY);
    const delta = xSvg - dragRef.current.x0;
    setTx(clampTx(dragRef.current.tx0 + delta, scale));
    setTooltipFromClient(e.clientX, e.clientY);
  }
  function onMouseUp(){ dragRef.current.active = false; }

  // ---------------- Mobile: press para tooltip, swipe para scroll ----------------
  const gestureRef = useRef({ down:false, x:0, y:0, timer:0, moved:false });
  const TAP_MOVE_TOL = 8, PRESS_MS = 180;

  function onPointerDown(e){
    if (!isMobile) return;
    const x = e.clientX ?? e.touches?.[0]?.clientX;
    const y = e.clientY ?? e.touches?.[0]?.clientY;
    if (x == null || y == null) return;
    gestureRef.current = { down:true, x, y, moved:false, timer:0 };
    const t = window.setTimeout(() => {
      if (gestureRef.current.down && !gestureRef.current.moved) setTooltipFromClient(x, y);
    }, PRESS_MS);
    gestureRef.current.timer = t;
  }
  function onPointerMove(e){
    if (!isMobile || !gestureRef.current.down) return;
    const x = e.clientX ?? e.touches?.[0]?.clientX;
    const y = e.clientY ?? e.touches?.[0]?.clientY;
    if (x == null || y == null) return;
    const dx = Math.abs(x - gestureRef.current.x);
    const dy = Math.abs(y - gestureRef.current.y);
    if (dx > TAP_MOVE_TOL || dy > TAP_MOVE_TOL) {
      gestureRef.current.moved = true; window.clearTimeout(gestureRef.current.timer);
      setTooltip(null); setTooltipPos(null); // esconda enquanto arrasta/rola
    }
  }
  function onPointerUp(e){
    if (!isMobile) return;
    const g = gestureRef.current;
    window.clearTimeout(g.timer);
    if (!g.moved) {
      const x = e.clientX ?? e.changedTouches?.[0]?.clientX;
      const y = e.clientY ?? e.changedTouches?.[0]?.clientY;
      if (x != null && y != null) setTooltipFromClient(x, y);
    }
    g.down = false;
  }

  // ---------------- Geometrias ----------------
  const tuPoints  = useMemo(() => containerData.map((d, i) => `${xAt(i)},${yTu(d.tu)}`).join(' '), [xAt]);
  const tkuPoints = useMemo(() => containerData.map((d, i) => `${xAt(i)},${yTku(d.tku)}`).join(' '), [xAt]);

  // ---------------- Estilos ----------------
  const gridStroke = 1.2;
  const lineStrokeTU = isMobile ? 4 : 5;
  const lineStrokeTKU = isMobile ? 3.6 : 4.6;
  const rTU = isMobile ? 4.5 : 5.5;
  const rTKU = isMobile ? 4.2 : 5.2;
  const fontAxis = isMobile ? 12 : 14;
  const fontLegend = isMobile ? 'text-base' : 'text-lg';

  const mobileContentWidth = Math.max(900, VB_W * Math.max(1, scale * 1.05));

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <div className="text-center">
            <div className="flex items-center justify-center gap-4 mb-4">
              <Container className="w-8 h-8 text-blue-600" />
              <CardTitle className="text-3xl font-bold text-gray-900">Movimentação de Contêineres</CardTitle>
              <Container className="w-8 h-8 text-blue-600" />
            </div>
            <p className="text-xl text-gray-600">Crescimento sustentado na movimentação ferroviária de contêineres</p>
          </div>
        </CardHeader>

        <CardContent>
          <div ref={scrollRef} className={isMobile ? 'overflow-x-auto no-scrollbar' : ''}>
            <div
              ref={containerRef}
              className="relative"
              style={{ width: isMobile ? `${mobileContentWidth}px` : '100%', height: '420px' }}
            >
              <svg
                ref={svgRef}
                viewBox={`0 0 ${VB_W} ${VB_H}`}
                className="w-full h-full select-none"
                preserveAspectRatio="xMidYMid meet"
                // desktop
                onMouseLeave={handleLeave}
                onWheel={handleWheel}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onMouseMove={onMouseMove}
                onMouseMoveCapture={(e)=>{ if(!isMobile) setTooltipFromClient(e.clientX, e.clientY); }}
                // mobile (não bloqueia o scroll horizontal)
                style={{ touchAction: isMobile ? "pan-x pinch-zoom" : "none" }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
              >
                {/* GRID + eixo Y */}
                {[0,1,2,3,4,5,6,7].map(val => {
                  const y = yTu(val);
                  return (
                    <g key={val}>
                      <line x1={X0} y1={y} x2={X1} y2={y} stroke="#e5e7eb" strokeWidth={gridStroke} />
                      <text x={X0-8} y={y+4} textAnchor="end" fontSize={fontAxis} fill="#6b7280">{val}</text>
                    </g>
                  );
                })}
                <text x={isMobile ? 10 : 18} y="170" transform={`rotate(-90, ${isMobile ? 10 : 18}, 170)`} textAnchor="middle" fontSize={12} fill="#6b7280">
                  Milhões de TU
                </text>

                {/* Eixo X (anos) transformado em X */}
                {containerData.map((d,i)=>(
                  <text key={d.year} x={tx + scale * xAt(i)} y={Y1 + 24} textAnchor="middle" fontSize={fontAxis} fill="#6b7280">
                    {d.year}
                  </text>
                ))}

                {/* Séries (transform só em X) */}
                <g transform={`translate(${tx},0) scale(${scale},1)`}>
                  <polyline points={tuPoints}  fill="none" stroke="#10b981" strokeWidth={lineStrokeTU}/>
                  {containerData.map((d,i)=>(<circle key={`tu-${i}`} cx={xAt(i)} cy={yTu(d.tu)} r={rTU} fill="#10b981" />))}
                  <polyline points={tkuPoints} fill="none" stroke="#0ea5e9" strokeWidth={lineStrokeTKU} opacity={0.96}/>
                  {containerData.map((d,i)=>(<circle key={`tku-${i}`} cx={xAt(i)} cy={yTku(d.tku)} r={rTKU} fill="#0ea5e9" />))}
                </g>

                {/* Scrubber */}
                {tooltip && (
                  <line
                    x1={tx + scale * xAt(tooltip.index)} y1={Y0}
                    x2={tx + scale * xAt(tooltip.index)} y2={Y1}
                    stroke="#9ca3af" strokeWidth="1.2" strokeDasharray="4"
                  />
                )}

                <rect x="0" y="0" width={VB_W} height={VB_H} fill="transparent" className="cursor-crosshair" />
              </svg>

              {/* Tooltip com clamp */}
              {tooltip && (
                <div
                  ref={tooltipRef}
                  className="absolute z-10 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg pointer-events-none"
                  style={{
                    left: tooltipPos ? tooltipPos.left : -9999,
                    top:  tooltipPos ? tooltipPos.top  : -9999,
                    transform: tooltipPos?.placeAbove ? "translate(-50%,-10%)" : "translate(-50%,10%)",
                    whiteSpace: "nowrap"
                  }}
                >
                  <div className="text-sm font-semibold">{tooltip.year}</div>
                  <div className="text-xs text-emerald-300">TU: {tooltip.tu.toFixed(1)} M</div>
                  <div className="text-xs text-sky-300">TKU: {tooltip.tku.toFixed(1)} B</div>
                </div>
              )}
            </div>
          </div>

          {/* Legenda */}
          <div className={`mt-4 flex justify-center gap-8 ${fontLegend}`}>
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 bg-emerald-500 rounded"></div>
              <span className="font-medium text-gray-800">Milhões de Toneladas Úteis (TU)</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 bg-sky-500 rounded"></div>
              <span className="font-medium text-gray-800">Bilhões de TKU</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Principais Rotas Ferroviárias de Contêineres (mantido) */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-blue-900 flex items-center gap-2">
              <Box className="w-5 h-5" />
              Rondonópolis - Cubatão
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-blue-800">MT → SP</span>
              <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs">13% do total</span>
            </div>
            <p className="text-sm text-blue-700">
              Maior terminal de grãos da América Latina. Concentra o escoamento das safras do Centro-Oeste para o Porto de Santos via Rumo Malha Norte.
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-green-900 flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Ortigueira - D. Pedro II
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-green-800">PR → PR</span>
              <span className="bg-green-600 text-white px-2 py-1 rounded text-xs">10% do total</span>
            </div>
            <p className="text-sm text-green-700">
              Projeto intermodal da Klabin para exportação de papel e celulose. Conecta fábrica de 2,5 milhões ton/ano ao TCP via Brado Logística.
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-orange-900 flex items-center gap-2">
              <Ship className="w-5 h-5" />
              Rondonópolis - Boa Vista Velha
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-orange-800">MT → SP</span>
              <span className="bg-orange-600 text-white px-2 py-1 rounded text-xs">7% do total</span>
            </div>
            <p className="text-sm text-orange-700">
              Rota em crescimento desde 2016. Operação conjunta entre Rumo Malha Paulista (RMP) e Ferrovia Centro Atlântico (FCA) em Campinas.
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-purple-900 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Cambé/Cascavel - D. Pedro II
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-purple-800">PR → PR</span>
              <span className="bg-purple-600 text-white px-2 py-1 rounded text-xs">Cargas refrigeradas</span>
            </div>
            <p className="text-sm text-purple-700">
              Terminais intermodais especializados em cargas refrigeradas, principalmente frango, além de café e fertilizantes da região.
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-teal-900 flex items-center gap-2">
              <ArrowRight className="w-5 h-5" />
              Criciúma - Imbituba
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-teal-800">SC → SC</span>
              <span className="bg-teal-600 text-white px-2 py-1 rounded text-xs">+26% ano</span>
            </div>
            <p className="text-sm text-teal-700">
              Via Ferrovia Tereza Cristina (FTC). Transporta arroz, contêineres vazios e outros produtos para o Porto de Imbituba.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
